import React, { useCallback, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  Templatestyle,
  Templateiconstyle,
  Templatetextstyle,
  TemplateSelectedLanguageRadioButton,
} from "../../atoms/ConnectStories";
import SelectLanguage from "../../../images/select_language.svg";
import { useTranslation } from "react-i18next";
import useApiRepository from "../../../api/useApiRepository";

export const LanguageSelection = ({ onLanguageChange }) => {
  const { t } = useTranslation();
  const apiRepo = useApiRepository();
  const { loginReducer, persistItemsReducer } = useSelector((state) => state);
  const [selectedLanguage, setSelectedLanguage] = useState("");

  // Memoized supported languages list
  const supportedLanguages = loginReducer?.getSOConfiguration?.connectScreenLanguageOptions?.supportedLanguages || [];

  // Check if language selection should be rendered
  const shouldShowLanguageSelection =
    loginReducer?.getSOConfiguration?.connectScreenLanguageOptions?.multipleLanguagesSupported ||
    supportedLanguages.length > 0;

  // Memoized language code getter with optimized dependencies
  const getLanguageCode = useCallback(
    (name, reverse = false) => {
      const match = supportedLanguages.find((item) =>
        reverse ? item.languageCode === name : item.languageName === name,
      );
      return reverse ? match?.languageName || "" : match?.languageCode || "en";
    },
    [supportedLanguages],
  );

  // Memoized handler for fetching templates and notifying parent (single source of truth)
  const fetchTemplatesAndNotify = useCallback(
    (languageName) => {
      const languageCode = getLanguageCode(languageName);
      console.log(`[Language Selection] Fetching templates for: ${languageName} (code: ${languageCode})`);
      
      // Single API call + notification (no duplicate calls)
      apiRepo.connectRepository.getWritingTemplates(languageCode, true);
      onLanguageChange?.({ languageName, languageCode });
    },
    [getLanguageCode, apiRepo, onLanguageChange]
  );

  // Initialize language on mount only
  useEffect(() => {
    const savedLang = localStorage.getItem("selectedLang");
    
    if (savedLang) {
      setSelectedLanguage(savedLang);
      fetchTemplatesAndNotify(savedLang);
    } else if (persistItemsReducer?.selectedSOData?.soCode === "AUTO" || 
               persistItemsReducer?.selectedSOData?.soCode === "FINO") {
      setSelectedLanguage("English");
      fetchTemplatesAndNotify("English");
    }
  }, []); // Empty dependency - mount only

  // Memoized language change handler - prevent inline function recreation
  const handleLanguageChange = useCallback(
    (languageName) => {
      console.log(`[Language Selection] User changed to: ${languageName}`);
      
      // Single batch update to prevent flicker
      setSelectedLanguage(languageName);
      localStorage.setItem("selectedLang", languageName);
      fetchTemplatesAndNotify(languageName);
    },
    [fetchTemplatesAndNotify]
  );

  return shouldShowLanguageSelection ? (
    <Templatestyle style={{ width: "100%" }}>
      <Templateiconstyle>
        <img src={SelectLanguage} alt="" />
      </Templateiconstyle>
      <Templatetextstyle style={{ paddingRight: "20px" }}>
        {t("SelectLanguage")}
      </Templatetextstyle>

      <TemplateSelectedLanguageRadioButton>
        {loginReducer?.getSOConfiguration?.connectScreenLanguageOptions?.supportedLanguages?.map(
          (ele) => {
            const soCode = persistItemsReducer?.selectedSOData?.soCode;
            let displayLanguage;

            if (soCode === "AUTO") {
              displayLanguage =
                ele.languageName === "German" ? "Deutsch" : "Englisch";
            } else if (soCode === "JPNO") {
              displayLanguage =
                ele.languageName === "Japanese" ? t("Japanese") : "English";
            } else {
              displayLanguage =
                ele.languageName === "Finnish" ? "suomi" : "englanti";
            }

            return (
              <span
                key={ele.languageName}
                style={{
                  display: "flex",
                  gap: "6px",
                  alignItems: "center",
                  marginLeft: "15px",
                }}
              >
                <input
                  type="radio"
                  name="language"
                  value={ele.languageName}
                  checked={ele.languageName === selectedLanguage}
                  onChange={() => handleLanguageChange(ele.languageName)}
                />
                <span>{displayLanguage}</span>
              </span>
            );
          },
        )}
      </TemplateSelectedLanguageRadioButton>
    </Templatestyle>
  ) : null;
};
